import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../services/api';
import { toast } from '../components/ThreatModelling/ui/use-toast';
import { error, log } from 'utils/logger';
import { joinUrl } from 'lib/utils';

interface CheckmarxContextType {
  checkmarxConnected: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  setCheckmarxConnected: (connected: boolean) => void;
  setIsConnecting: (connecting: boolean) => void;
  setIsDisconnecting: (disconnecting: boolean) => void;
}

const CheckmarxContext = createContext<CheckmarxContextType | undefined>(undefined);

export const CheckmarxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [checkmarxConnected, setCheckmarxConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders()
      if (!headers) {
        error('Auth token not found');
        setCheckmarxConnected(false);
        setIsLoading(false);
        return;
      }

      log('Checking GitHub auth status...');
      const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/check-auth'), {
        headers
      });

      const data = response.data;
      log('GitHub auth check response:', data);
      
      setCheckmarxConnected(data.message === "Authenticated");
      
      // Show error toast if there are unmapped projects
      if (data.error?.includes('unmapped projects')) {
        toast({
          variant: "destructive",
          title: "Checkmarx",
          description: data.error
        });
      }
    } catch (err : any) {
      error('Error checking GitHub auth:', err);
      setCheckmarxConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only check auth if we have auth headers
    const headers = getAuthHeaders();
    if (headers) {
      checkAuth();
    } else {
      setIsLoading(false);
      setCheckmarxConnected(false);
    }
  }, [checkAuth]);

  return (
    <CheckmarxContext.Provider value={{
      checkmarxConnected,
      isConnecting,
      isDisconnecting,
      isLoading,
      checkAuth,
      setCheckmarxConnected,
      setIsConnecting,
      setIsDisconnecting
    }}>
      {children}
    </CheckmarxContext.Provider>
  );
};

export const useCheckmarx = () => {
  const context = useContext(CheckmarxContext);
  if (context === undefined) {
    throw new Error('useCheckmarx must be used within a CheckmarxProvider');
  }
  return context;
};
